// src/pages/sales/purchase-order-components/PODeliveryConfirmation.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  LinearProgress,
  alpha,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as ShippingIcon,
  CloudUpload as UploadIcon,
  VerifiedUser as ApprovalIcon,
  Schedule as ScheduleIcon,
  RequestQuote as RequestQuoteIcon
} from '@mui/icons-material';
import { usePermissions } from '../../../utils/permissions';

// Import section components
import DocumentsSection from './pi-delivery-confirmation-components/DocumentsSection';
import InventoryUsageSection from './pi-delivery-confirmation-components/InventoryUsageSection';
import DeliveryDetailsSection from './pi-delivery-confirmation-components/DeliveryDetailsSection';
import PaymentSection from './pi-delivery-confirmation-components/PaymentSection';
import FinalApprovalSection from './pi-delivery-confirmation-components/FinalApprovalSection';

// Styled components (matching PurchaseOrdersTabs)
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

// Custom Timeline Connector
const TimelineConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    height: 4,
    border: 0,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    transition: 'all 0.3s ease'
  }
}));

// Custom Timeline Step Icon
const TimelineStepIcon = styled('div')(({ theme, active, completed, stepIndex }) => {
  const colors = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main
  ];
  const bgColor = completed ? theme.palette.success.main : active ? colors[stepIndex] : '#e0e0e0';
  
  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: bgColor,
    border: active ? `3px solid ${alpha(colors[stepIndex] || '#1976d2', 0.3)}` : '3px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: completed || active ? 'white' : '#999',
    transition: 'all 0.3s ease',
    position: 'relative',
    cursor: 'pointer',
    boxShadow: active ? `0 0 20px ${alpha(colors[stepIndex] || '#1976d2', 0.4)}` : '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `0 6px 20px ${alpha(bgColor || '#e0e0e0', 0.4)}`
    },
    '& .MuiSvgIcon-root': {
      fontSize: 28
    }
  };
});

// Timeline Component
function DeliveryTimeline({ activeStep, onStepClick, completedSteps, visibleSteps = [] }) {
  const theme = useTheme();
  
  const allTimelineSteps = [
    {
      label: 'Documents',
      sublabel: 'Photos & Files',
      icon: <UploadIcon />,
      description: 'Upload delivery documents and photos',
      completed: completedSteps?.documents || false
    },
    {
      label: 'Inventory Usage',
      sublabel: 'Deduct Stock',
      icon: <ScheduleIcon />,
      description: 'Deduct sold products from inventory',
      completed: completedSteps?.inventoryUsage || false
    },
    {
      label: 'Invoice & Payment',
      sublabel: 'Billing Info',
      icon: <RequestQuoteIcon />,
      description: 'Manage invoice and payment details',
      completed: completedSteps?.payment || false
    },
    {
      label: 'Delivery Details',
      sublabel: 'Customer Receipt',
      icon: <ShippingIcon />,
      description: 'Record delivery information',
      completed: completedSteps?.deliveryDetails || false
    },
    {
      label: 'Final Approval',
      sublabel: 'Supervisor Sign-off',
      icon: <ApprovalIcon />,
      description: 'Approve delivery confirmation',
      completed: completedSteps?.approval || false
    }
  ];

  // Filter steps based on visibleSteps array
  const timelineSteps = allTimelineSteps.filter((_, index) => visibleSteps.includes(index));

  return (
    <Box sx={{ 
      p: 3,
      borderRadius: 4,
      mb: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'success.main', mb: 1 }}>
            Delivery Confirmation Workflow
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Complete each section to finalize delivery confirmation
          </Typography>
        </Box>
        <Box sx={{ 
          px: 3, 
          py: 1, 
          backgroundColor: alpha(theme.palette.success.main, 0.08),
          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          borderRadius: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
            Section {visibleSteps.indexOf(activeStep) + 1} of {visibleSteps.length}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 3, 
        borderRadius: 3, 
        mb: 3, 
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[400], 0.9)}`
      }}>
        <Stepper 
          activeStep={visibleSteps.indexOf(activeStep)} 
          connector={<TimelineConnector />}
          sx={{ 
            '& .MuiStepLabel-root': {
              cursor: 'pointer'
            }
          }}
        >
          {timelineSteps.map((step, displayIndex) => {
            const actualIndex = visibleSteps[displayIndex];
            const isActive = actualIndex === activeStep;
            const isCompleted = step.completed;
            
            return (
              <Step key={step.label} onClick={() => onStepClick(actualIndex)}>
                <StepLabel
                  StepIconComponent={() => (
                    <TimelineStepIcon 
                      active={isActive} 
                      completed={isCompleted}
                      stepIndex={displayIndex}
                    >
                      {isCompleted ? <CheckCircleIcon /> : step.icon}
                    </TimelineStepIcon>
                  )}
                >
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: isActive ? 'success.main' : 'text.secondary',
                        mb: 0.5,
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isActive ? 'success.main' : 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      {step.sublabel}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        display: 'block',
                        maxWidth: 140,
                        lineHeight: 1.3
                      }}
                    >
                      {step.description}
                    </Typography>
                    
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: alpha(
                            isCompleted ? theme.palette.success.main : 
                            isActive ? theme.palette.warning.main : 
                            theme.palette.grey[500], 0.1
                          ),
                          borderRadius: 1,
                          border: `1px solid ${alpha(
                            isCompleted ? theme.palette.success.main : 
                            isActive ? theme.palette.warning.main : 
                            theme.palette.grey[500], 0.2
                          )}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        {isCompleted && <CheckCircleIcon sx={{ fontSize: 12 }} />}
                        {isActive && !isCompleted && <ScheduleIcon sx={{ fontSize: 12 }} />}
                        {isCompleted ? 'Completed' : isActive ? 'In Progress' : 'Pending'}
                      </Typography>
                    </Box>
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => {
            const currentIndex = visibleSteps.indexOf(activeStep);
            if (currentIndex > 0) {
              onStepClick(visibleSteps[currentIndex - 1]);
            }
          }}
          disabled={visibleSteps.indexOf(activeStep) === 0}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 500,
            '&:disabled': {
              borderColor: alpha(theme.palette.grey[400], 0.5)
            }
          }}
        >
          ← Previous Section
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {timelineSteps.map((step, displayIndex) => {
            const actualIndex = visibleSteps[displayIndex];
            return (
              <Box
                key={displayIndex}
                onClick={() => onStepClick(actualIndex)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: actualIndex === activeStep ? 'success.main' : alpha(theme.palette.grey[400], 0.5),
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.3)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                  }
                }}
              />
            );
          })}
        </Box>

        <Button
          variant="contained"
          onClick={() => {
            const currentIndex = visibleSteps.indexOf(activeStep);
            if (currentIndex < visibleSteps.length - 1) {
              onStepClick(visibleSteps[currentIndex + 1]);
            }
          }}
          disabled={visibleSteps.indexOf(activeStep) === visibleSteps.length - 1}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 500,
            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            '&.Mui-disabled': {
              background: alpha(theme.palette.grey[400], 0.3),
              color: alpha(theme.palette.text.secondary, 0.5),
              border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
            }
          }}
        >
          Next Section →
        </Button>
      </Box>
    </Box>
  );
}

const PODeliveryConfirmation = () => {
  const { companyId, id: poId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // Permission checks
  const { 
    canViewDeliveryDocuments, 
    canViewInventoryUsage, 
    canViewInvoicePayment, 
    canViewDeliveryDetails, 
    canViewFinalApproval 
  } = usePermissions();

  // Determine which steps are visible based on permissions
  const getVisibleSteps = () => {
    const steps = [];
    if (canViewDeliveryDocuments()) steps.push(0); // Documents
    if (canViewInventoryUsage()) steps.push(1); // Inventory Usage
    if (canViewInvoicePayment()) steps.push(2); // Invoice & Payment
    if (canViewDeliveryDetails()) steps.push(3); // Delivery Details
    if (canViewFinalApproval()) steps.push(4); // Final Approval
    return steps;
  };

  const visibleSteps = getVisibleSteps();
  const firstAvailableStep = visibleSteps.length > 0 ? visibleSteps[0] : 0;

  // State
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(firstAvailableStep);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [conditionOfGoods, setConditionOfGoods] = useState('');
  const [deliveryDetails, setDeliveryDetails] = useState({
    actualDeliveryDate: '',
    actualDeliveryTime: '',
    deliveryComplete: 'yes',
    shortageDetails: '',
    quantityShort: '',
    conditionWhenDelivered: 'good',
    conditionNotes: '',
    issueSeverity: 'none',
    resolutionRequired: 'no',
    resolutionNotes: ''
  });
  const [payment, setPayment] = useState({
    invoiceSent: 'no',
    invoiceDate: '',
    invoiceNumber: '',
    paymentCollected: 'no',
    paymentAmount: '',
    paymentMethod: '',
    additionalNotes: ''
  });
  const [approval, setApproval] = useState({
    approved: false,
    approvedBy: null,
    approvedAt: null,
    notes: ''
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [completionFlags, setCompletionFlags] = useState({
    documents: false,
    inventoryUsage: false,
    deliveryDetails: false,
    payment: false,
    approval: false
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Load PO Data
  useEffect(() => {
    loadPOData();
  }, [poId]);

  const loadPOData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customer_purchase_orders')
        .select(`
          *,
          customer:customer_id(name, company),
          po_creation:po_creation!purchase_order_id(po_number, total_amount)
        `)
        .eq('id', poId)
        .single();

      if (error) throw error;
      setPurchaseOrder(data);

      // Load completion flags from delivery confirmation
      await loadCompletionFlags();

    } catch (err) {
      console.error('Error loading PO:', err);
      showSnackbar('Failed to load purchase order: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCompletionFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('po_delivery_confirmations')
        .select('documents_completed, inventory_usage_completed, delivery_details_completed, post_delivery_completed, final_approval_completed, approved')
        .eq('purchase_order_id', poId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // Ignore "not found" error
      
      if (data) {
        setCompletionFlags({
          documents: data.documents_completed || false,
          inventoryUsage: data.inventory_usage_completed || false,
          deliveryDetails: data.delivery_details_completed || false,
          payment: data.post_delivery_completed || false,
          approval: data.final_approval_completed || data.approved || false
        });
      }
    } catch (err) {
      console.error('Error loading completion flags:', err);
    }
  };

  const handleSectionClick = (sectionIndex) => {
    // Only allow navigation to visible steps
    if (visibleSteps.includes(sectionIndex)) {
      setActiveSection(sectionIndex);
    }
  };

  // Calculate completed steps - using database flags
  const getCompletedSteps = () => {
    return completionFlags;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      // TODO: Save delivery confirmation as draft
      // For now, just showing a message
      showSnackbar('Draft saved successfully! (UI-only for now)', 'success');
    } catch (err) {
      console.error('Error saving draft:', err);
      showSnackbar('Failed to save draft: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (signatureData) => {
    setSaving(true);
    try {
      // TODO: Save complete delivery confirmation and update PO status
      // For now, just showing a success message
      showSnackbar('Delivery confirmation approved successfully! (UI-only for now)', 'success');
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate(`/company/${companyId}/proforma-invoices`);
      }, 2000);
    } catch (err) {
      console.error('Error approving:', err);
      showSnackbar('Failed to approve: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/company/${companyId}/proforma-invoices`);
  };

  const handleBackToOrder = () => {
    navigate(`/company/${companyId}/proforma-invoices/${poId}`);
  };

  const renderSectionComponent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      );
    }

    switch (activeSection) {
      case 0:
        return <DocumentsSection 
          documents={documents} 
          setDocuments={setDocuments}
          conditionOfGoods={conditionOfGoods}
          setConditionOfGoods={setConditionOfGoods}
          onSave={loadCompletionFlags}
        />;
      case 1:
        return <InventoryUsageSection 
          onSave={loadCompletionFlags}
        />;
      case 2:
        return <PaymentSection 
          payment={payment} 
          setPayment={setPayment}
          onSave={loadCompletionFlags}
        />;
      case 3:
        return <DeliveryDetailsSection 
          deliveryDetails={deliveryDetails} 
          setDeliveryDetails={setDeliveryDetails}
          onSave={loadCompletionFlags}
        />;
      case 4:
        return <FinalApprovalSection 
          approval={approval} 
          setApproval={setApproval}
          onApprove={handleApprove}
          onSave={loadCompletionFlags}
        />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <ShippingIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Delivery Confirmation
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Retrieving your delivery confirmation data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const completedSteps = getCompletedSteps();

  return (
    <Box>
      <Container maxWidth="xl" sx={{ py: 3, pb: 12 }}>
        {/* Back to Orders */}
        <Box sx={{ mt: 5, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="text"
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              px: 1,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
          >
            Back to Orders Page
          </Button>
        </Box>

        {/* Enhanced Page Header (matching PurchaseOrdersTabs) */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 0,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
            opacity: 0.8
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                <Box sx={{
                  p: 2,
                  borderRadius: 3,
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                }}>
                  <RequestQuoteIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{
                    fontWeight: 500,
                    color: 'primary.main',
                    mb: 0.5,
                    fontSize: '2rem',
                    letterSpacing: '-0.5px'
                  }}>
                    {purchaseOrder?.po_creation?.[0]?.po_number || purchaseOrder?.po_number || 'Purchase Order'}
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}>
                    Delivery Confirmation - {purchaseOrder?.customer?.name || 'Customer'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  startIcon={<RequestQuoteIcon />}
                  onClick={handleBackToOrder}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: alpha(theme.palette.primary.main, 0.08)
                    }
                  }}
                >
                  Back to Order
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              maxWidth: 600,
              lineHeight: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              Complete delivery confirmation and approval workflow for this purchase order.
            </Typography>
          </CardContent>
        </Card>

        {/* Timeline Component */}
        <DeliveryTimeline
          activeStep={activeSection}
          onStepClick={handleSectionClick}
          completedSteps={completedSteps}
          visibleSteps={visibleSteps}
        />

        {/* Main Content Area */}
        <SectionPaper>
          {renderSectionComponent()}
        </SectionPaper>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default PODeliveryConfirmation;